import React, { useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';

export type GuideAccordionItem = Readonly<{
  id: string;
  title: string;
  chipLabel?: string;
  details: React.ReactNode;
}>;

type GuideAccordionListProps = Readonly<{
  idPrefix: string;
  items: readonly GuideAccordionItem[];
  detailsSx?: SxProps<Theme>;
}>;

/**
 * Divider-separated accordion list for guide pages: single item open at a time,
 * plus/minus expand icons.
 */
export const GuideAccordionList: React.FC<GuideAccordionListProps> = ({
  idPrefix,
  items,
  detailsSx,
}) => {
  const [expandedId, setExpandedId] = useState<string | false>(false);

  const handleChange = (itemId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedId(isExpanded ? itemId : false);
  };

  return (
    <>
      {items.map((item, index) => (
        <React.Fragment key={item.id}>
          {index > 0 && <Divider />}
          <Accordion
            id={`${idPrefix}-${item.id}`}
            expanded={expandedId === item.id}
            onChange={handleChange(item.id)}
            disableGutters
            elevation={0}
            sx={accordionSx}
          >
            <AccordionSummary
              id={`${idPrefix}-summary-${item.id}`}
              expandIcon={
                <AppIcon
                  iconName={expandedId === item.id ? 'MinusCircle' : 'PlusCircle'}
                  diameter="24px"
                  color="Colors/Foreground/fg-quaternary"
                />
              }
              sx={summarySx}
            >
              {item.chipLabel ? (
                <Box sx={titleRowSx}>
                  <Chip
                    label={item.chipLabel}
                    variant="badge"
                    size="sm"
                    type="default"
                    sx={chipSx}
                  />
                  <Typography variant="text-lg-semibold" sx={titleSx}>
                    {item.title}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="text-lg-semibold" sx={titleSx}>
                  {item.title}
                </Typography>
              )}
            </AccordionSummary>
            <AccordionDetails sx={[baseDetailsSx, detailsSx] as SxProps<Theme>}>
              {item.details}
            </AccordionDetails>
          </Accordion>
        </React.Fragment>
      ))}
    </>
  );
};

const accordionSx: SxProps<Theme> = {
  backgroundColor: 'transparent',
  backgroundImage: 'none',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
};

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  px: 0,
  py: backrs.spacing['spacing-xl'],
  transition: 'padding-bottom 0.2s ease',
  '&.Mui-expanded': {
    pb: backrs.spacing['spacing-md'],
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    transition: 'none',
    '&.Mui-expanded': {
      transform: 'none',
    },
  },
  '& .MuiTypography-root, & .MuiAccordionSummary-expandIconWrapper': {
    transition: 'opacity 0.15s ease-in-out',
  },
  '&:hover .MuiTypography-root, &:hover .MuiAccordionSummary-expandIconWrapper': {
    opacity: 0.7,
  },
});

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Brand/200']}`,
  backgroundColor: backrs.colors['Brand/50'],
  color: backrs.colors['Brand/700'],
  cursor: 'default',
  '&:hover': {
    backgroundColor: backrs.colors['Brand/50'],
  },
});

const titleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const baseDetailsSx: SxProps<Theme> = ({ backrs }) => ({
  px: 0,
  pt: 0,
  pb: backrs.spacing['spacing-xl'],
});
